import {
  useLazyGetNomadicStatusQuery,
  useSetNomadicMutation,
  useSetStaticMutation,
} from "./nomadicApi";
import { useErrorNotification } from "@/react/notifications";
import { useEffect, useState } from "react";
import PageLayout from "@/react/features/styled/PageLayout";
import { CustomerCard } from "@/react/features/customer";
import { useSelector } from "react-redux";
import { customerIdSelector } from "@/react/features/customer/customerSlice";
import { OverlaySpinner } from "@/react/components/OverlaySpinner";
import PageContentContainer from "@/react/features/styled/PageContentContainer";
import PageTitle from "@/react/components/PageTitle";
import { formatUnixTimestamp } from "@/react/datetime";
import {
  ActionButton,
  ActionsContainer,
  ContainerBody,
  LastRefreshSpan,
  Paragraph,
  StyledPlayIcon,
  StyledRefreshIcon,
} from "./styled";
import PhoneWithStarIcon from "@8x8/oxygen-icon/icons/PhoneWithStarIcon";
import { notify, TYPE } from "@8x8/oxygen-toaster";

const STATES = Object.freeze({
  UNKNOWN: "UNKNOWN",
  NOMADIC: "NOMADIC",
  STATIC: "STATIC",
  MIGRATING_TO_NOMADIC: "MIGRATING_TO_NOMADIC",
  MIGRATING_TO_STATIC: "MIGRATING_TO_STATIC",
  MIGRATION_TO_NOMADIC_FAILED: "MIGRATION_TO_NOMADIC_FAILED",
  MIGRATION_TO_STATIC_FAILED: "MIGRATION_TO_STATIC_FAILED",
});

const CAN_MIGRATE_TO_NOMADIC_STATES = Object.freeze([
  STATES.UNKNOWN,
  STATES.STATIC,
  STATES.MIGRATING_TO_STATIC,
  STATES.MIGRATION_TO_STATIC_FAILED,
  STATES.MIGRATION_TO_NOMADIC_FAILED,
]);
const CAN_MIGRATE_TO_STATIC_STATES = Object.freeze([
  STATES.UNKNOWN,
  STATES.NOMADIC,
  STATES.MIGRATING_TO_NOMADIC,
  STATES.MIGRATION_TO_NOMADIC_FAILED,
  STATES.MIGRATION_TO_STATIC_FAILED,
]);

const formatLastChangeDate = (date) => {
  const unixTimestamp = new Date(date).getTime();
  return isNaN(unixTimestamp) ? null : formatUnixTimestamp(unixTimestamp);
};

const NomadicPage = () => {
  const customerId = useSelector(customerIdSelector);
  const [getStatus, { data: status, error: fetchError, isFetching }] =
    useLazyGetNomadicStatusQuery();
  const [setNomadic, { isLoading: isSettingNomadic, error: setNomadicError }] =
    useSetNomadicMutation();
  const [setStatic, { isLoading: isSettingStatic, error: setStaticError }] =
    useSetStaticMutation();

  useErrorNotification(fetchError || setNomadicError || setStaticError);
  useEffect(() => {
    getStatus();
  }, [customerId]);
  useEffect(() => {
    setLastRefreshed(Date.now());
  }, [status]);

  const [lastRefreshed, setLastRefreshed] = useState(null);

  const onChangeToNomadic = () => {
    const provider = status?.nomadicStatus?.provider;
    if (!provider) {
      notify({
        content: `Cannot set to nomadic with provider value '${provider}'`,
        type: TYPE.error,
      });
    } else {
      setNomadic({ provider });
    }
  };

  const onChangeToStatic = () => {
    setStatic();
  };

  const onRefresh = () => {
    getStatus().finally(() => setLastRefreshed(Date.now()));
  };

  const isLoading = isFetching || isSettingNomadic || isSettingStatic;

  const currentState = status?.nomadicStatus?.currentState;

  return (
    <PageLayout>
      {isLoading && <OverlaySpinner />}
      <CustomerCard />
      <PageContentContainer>
        <PageTitle>
          <PhoneWithStarIcon /> Nomadic 911
        </PageTitle>
        {!customerId && (
          <h6>You need to select a customer before updating Nomadic status.</h6>
        )}
        {fetchError && (
          <h6>
            Error fetching Nomadic911 status. Check the customer and try again.
          </h6>
        )}
        {customerId && !fetchError && (
          <ContainerBody>
            <Paragraph>
              <strong>MENTIONS</strong>
              <ul>
                <li>
                  Opt-in/out of the Nomadic E911 service for the selected
                  customer
                </li>
                <li>
                  Last changed date is displayed in UTC timezone. All
                  opt-ins/outs will be logged in the audit trail
                </li>
              </ul>
            </Paragraph>
            <Paragraph>
              <strong>CURRENT STATE: </strong>
              <code>{currentState}</code>
            </Paragraph>
            <Paragraph>
              <small>
                Sub Account Id: <code>{status?.subAccount?.subAccountId}</code>
              </small>
            </Paragraph>
            <Paragraph>
              <small>
                Last change:{" "}
                <code>
                  {formatLastChangeDate(status?.nomadicStatus?.lastChange)}
                </code>
              </small>
            </Paragraph>
            <ActionsContainer>
              {CAN_MIGRATE_TO_STATIC_STATES.includes(currentState) && (
                <ActionButton onClick={onChangeToStatic}>
                  <StyledPlayIcon invertColors />
                  Switch to STATIC
                </ActionButton>
              )}
              {CAN_MIGRATE_TO_NOMADIC_STATES.includes(currentState) && (
                <ActionButton onClick={onChangeToNomadic}>
                  <StyledPlayIcon invertColors />
                  Switch to NOMADIC
                </ActionButton>
              )}
              <ActionButton onClick={onRefresh}>
                <StyledRefreshIcon invertColors size={16} />
                Refresh
              </ActionButton>
              <LastRefreshSpan>
                <strong>Last Refresh: </strong>
                <code>{currentState}</code> (at{" "}
                {formatUnixTimestamp(lastRefreshed)})
              </LastRefreshSpan>
            </ActionsContainer>
          </ContainerBody>
        )}
      </PageContentContainer>
    </PageLayout>
  );
};

export default NomadicPage;
